import React, { FC, useState } from 'react';
import { 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle, 
  Button as MuiButton
} from '@mui/material';
import Button from '../../shared/Button/Button';

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  itemType: 'image' | 'video' | 'brand';
  title?: string;
  message?: string;
}

const DeleteConfirmationModal: FC<DeleteConfirmationModalProps> = ({
  isOpen, 
  onClose, 
  onConfirm, 
  itemType,
  title,
  message
}) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await onConfirm();
    } catch (error) {
      console.error('Error deleting item:', error);
    } finally {
      setIsDeleting(false);
      onClose();
    }
  };

  const getConfirmationMessage = () => {
    if (message) {
      return message;
    }

    if (itemType === 'video') {
      return 'Are you sure you want to delete this "Showroom Video"? This action cannot be undone.';
    }
    if (itemType === 'brand') {
      return 'Are you sure you want to delete this brand profile? This action cannot be undone.';
    }
    return 'Are you sure you want to delete this "Image"? This action cannot be undone.';
  };

  const getDialogTitle = () => {
    if (title) {
      return title;
    }

    if (itemType === 'brand') {
      return 'Delete Brand Profile';
    }
    return 'Confirm Delete';
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby="delete-confirmation-dialog"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle 
        id="delete-confirmation-dialog" 
        className="text-xl font-semibold text-black"
      >
        {getDialogTitle()}
      </DialogTitle>

      <DialogContent>
        <DialogContentText 
          className="text-black"
        >
          {getConfirmationMessage()}
        </DialogContentText>
      </DialogContent>

      <DialogActions className="p-4 flex justify-end space-x-3">
        <MuiButton 
          onClick={onClose} 
          className="text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
          disabled={isDeleting}
        >
          Cancel
        </MuiButton>
        <Button
          onClick={handleConfirmDelete}
          className="bg-red-600 hover:bg-red-700 text-white"
          loading={isDeleting}
          sizeClass="px-4 py-2"
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmationModal;
